import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Export dashboard to PDF - Smart pagination to avoid cutting widgets
 * Method: Capture entire dashboard as one image, then find smart page break points
 * @param {string} elementId - ID of the DOM element to export
 * @param {string} filename - Name of the PDF file
 * @param {object} options - Additional options
 */
export const exportToPDF = async (elementId, filename = 'report.pdf', options = {}) => {
    try {
        const element = document.getElementById(elementId);
        if (!element) {
            console.error(`Element with id "${elementId}" not found`);
            return;
        }

        // Show loading state if callback provided
        if (options.onStart) {
            options.onStart();
        }

        // Capture the ENTIRE dashboard as one image
        const canvas = await html2canvas(element, {
            scale: 2, // Higher quality
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            ...options.canvasOptions
        });

        // Get widget positions to find safe page break points
        const widgets = element.querySelectorAll('[class*="react-grid-item"]');
        const containerRect = element.getBoundingClientRect();

        // Collect widget Y boundaries (top and bottom positions)
        const widgetBoundaries = Array.from(widgets).map(widget => {
            const rect = widget.getBoundingClientRect();
            return {
                top: rect.top - containerRect.top,
                bottom: rect.bottom - containerRect.top
            };
        });

        // PDF dimensions
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidthMM = 210;
        const pageHeightMM = 297;
        const marginMM = 5;
        const headerHeightMM = 20; // Space for title and date
        const contentWidthMM = pageWidthMM - (marginMM * 2);
        const contentHeightMM = pageHeightMM - (marginMM * 2) - headerHeightMM;

        // Calculate scale factor (pixels to mm)
        const imgWidthPx = canvas.width;
        const imgHeightPx = canvas.height;
        const scale = contentWidthMM / (imgWidthPx / 2); // Divide by 2 because canvas scale is 2
        const imgHeightMM = (imgHeightPx / 2) * scale;

        // Convert page height to pixels (at original element scale)
        const pageHeightPx = contentHeightMM / scale;

        // Find safe page break points (positions where no widget is cut)
        const safeBreakPoints = findSafePageBreaks(widgetBoundaries, pageHeightPx, containerRect.height);

        // Generate PDF pages
        const imgData = canvas.toDataURL('image/png');
        let currentPage = 0;
        const totalPages = safeBreakPoints.length;

        // Get entity name and date for header
        const entityName = options.scopeName || 'Default';
        const exportDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        for (let i = 0; i < safeBreakPoints.length; i++) {
            const startY = i === 0 ? 0 : safeBreakPoints[i - 1];
            const endY = safeBreakPoints[i];

            if (currentPage > 0) {
                pdf.addPage();
            }

            // Add header on each page
            pdf.setFontSize(14);
            pdf.setTextColor(0, 120, 212); // Blue
            pdf.text(`Analytics Report - Entity: ${entityName}`, marginMM, 12);

            pdf.setFontSize(9);
            pdf.setTextColor(100, 100, 100);
            pdf.text(exportDate, marginMM, 18);

            pdf.setFontSize(8);
            pdf.text(`Page ${currentPage + 1} / ${totalPages}`, pageWidthMM - marginMM - 25, 18);

            // Draw line under header
            pdf.setDrawColor(200, 200, 200);
            pdf.line(marginMM, 22, pageWidthMM - marginMM, 22);

            // Calculate source and destination coordinates
            const srcY = startY * 2; // Multiply by scale (2)
            const srcHeight = (endY - startY) * 2;

            // Create a cropped canvas for this page section
            const pageCanvas = document.createElement('canvas');
            pageCanvas.width = canvas.width;
            pageCanvas.height = srcHeight;
            const ctx = pageCanvas.getContext('2d');
            ctx.drawImage(canvas, 0, srcY, canvas.width, srcHeight, 0, 0, canvas.width, srcHeight);

            const pageImgData = pageCanvas.toDataURL('image/png');
            const pageImgHeightMM = (srcHeight / 2) * scale;

            // Add cropped image to PDF (after header)
            pdf.addImage(pageImgData, 'PNG', marginMM, marginMM + headerHeightMM, contentWidthMM, pageImgHeightMM);

            currentPage++;
        }

        // Add metadata
        pdf.setProperties({
            title: filename,
            subject: 'Analytics Report',
            author: 'Call Center Analytics',
            keywords: 'analytics, report, call center',
            creator: 'Admin Portal'
        });

        // Save PDF
        pdf.save(filename);

        // Success callback
        if (options.onSuccess) {
            options.onSuccess();
        }

        return pdf;
    } catch (error) {
        console.error('Error exporting to PDF:', error);
        if (options.onError) {
            options.onError(error);
        }
        throw error;
    }
};

/**
 * Find safe page break points where no widget will be cut
 * @param {Array} widgetBoundaries - Array of {top, bottom} for each widget
 * @param {number} pageHeightPx - Height of one page in pixels
 * @param {number} totalHeightPx - Total height of the content in pixels
 * @returns {Array} Array of Y positions (in pixels) where pages should end
 */
function findSafePageBreaks(widgetBoundaries, pageHeightPx, totalHeightPx) {
    const breakPoints = [];
    let currentPosition = 0;

    while (currentPosition < totalHeightPx) {
        // Ideal next page break
        let idealBreak = currentPosition + pageHeightPx;

        if (idealBreak >= totalHeightPx) {
            // Last page - include everything
            breakPoints.push(totalHeightPx);
            break;
        }

        // Find the best safe break point
        let safeBreak = idealBreak;

        // Check ALL widgets that might be cut at the ideal break position
        for (const widget of widgetBoundaries) {
            // Widget is cut if break point is between its top and bottom
            if (idealBreak > widget.top && idealBreak < widget.bottom) {
                // This widget would be cut - we must break BEFORE it
                const breakBeforeWidget = widget.top - 5;

                // Only use this break if it's after current position and leaves reasonable content
                if (breakBeforeWidget > currentPosition + 50) {
                    // Take the earliest safe break (in case multiple widgets would be cut)
                    if (breakBeforeWidget < safeBreak) {
                        safeBreak = breakBeforeWidget;
                    }
                }
            }
        }

        // Ensure minimum content per page
        if (safeBreak <= currentPosition + 50) {
            safeBreak = idealBreak; // Force break even if widget is cut
        }

        breakPoints.push(safeBreak);
        currentPosition = safeBreak;
    }

    return breakPoints;
}

/**
 * Export multiple widgets or sections to a single PDF
 * @param {Array} elementIds - Array of element IDs to export
 * @param {string} filename - Name of the PDF file
 * @param {object} options - Additional options
 */
export const exportMultipleToPDF = async (elementIds, filename = 'report.pdf', options = {}) => {
    try {
        if (options.onStart) {
            options.onStart();
        }

        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210;
        let isFirstPage = true;

        for (const elementId of elementIds) {
            const element = document.getElementById(elementId);
            if (!element) {
                console.warn(`Element with id "${elementId}" not found, skipping...`);
                continue;
            }

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                ...options.canvasOptions
            });

            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            const imgData = canvas.toDataURL('image/png');

            if (!isFirstPage) {
                pdf.addPage();
            }

            pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
            isFirstPage = false;
        }

        // Add metadata
        pdf.setProperties({
            title: filename,
            subject: 'Analytics Report',
            author: 'Call Center Analytics',
            keywords: 'analytics, report, call center',
            creator: 'Admin Portal'
        });

        pdf.save(filename);

        if (options.onSuccess) {
            options.onSuccess();
        }

        return pdf;
    } catch (error) {
        console.error('Error exporting multiple elements to PDF:', error);
        if (options.onError) {
            options.onError(error);
        }
        throw error;
    }
};

/**
 * Export with custom header and footer
 * @param {string} elementId - ID of the DOM element to export
 * @param {string} filename - Name of the PDF file
 * @param {object} options - Additional options including header/footer config
 */
export const exportToPDFWithHeaderFooter = async (elementId, filename = 'report.pdf', options = {}) => {
    try {
        const element = document.getElementById(elementId);
        if (!element) {
            console.error(`Element with id "${elementId}" not found`);
            return;
        }

        if (options.onStart) {
            options.onStart();
        }

        const canvas = await html2canvas(element, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            ...options.canvasOptions
        });

        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgData = canvas.toDataURL('image/png');

        // Add header
        if (options.header) {
            pdf.setFontSize(16);
            pdf.setTextColor(40, 40, 40);
            pdf.text(options.header.title || 'Analytics Report', 10, 15);

            if (options.header.subtitle) {
                pdf.setFontSize(10);
                pdf.setTextColor(100, 100, 100);
                pdf.text(options.header.subtitle, 10, 22);
            }
        }

        // Add content (with offset for header)
        const contentY = options.header ? 30 : 0;
        const availableHeight = pageHeight - contentY - (options.footer ? 15 : 0);
        const scaledImgHeight = Math.min(imgHeight, availableHeight);

        pdf.addImage(imgData, 'PNG', 0, contentY, imgWidth, scaledImgHeight);

        // Add footer
        if (options.footer) {
            const footerY = pageHeight - 10;
            pdf.setFontSize(8);
            pdf.setTextColor(150, 150, 150);

            const now = new Date();
            const dateStr = now.toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });

            pdf.text(`Generated: ${dateStr}`, 10, footerY);

            if (options.footer.pageNumbers) {
                pdf.text(`Page 1`, imgWidth - 30, footerY);
            }
        }

        pdf.setProperties({
            title: filename,
            subject: 'Analytics Report',
            author: 'Call Center Analytics',
            keywords: 'analytics, report, call center',
            creator: 'Admin Portal'
        });

        pdf.save(filename);

        if (options.onSuccess) {
            options.onSuccess();
        }

        return pdf;
    } catch (error) {
        console.error('Error exporting to PDF with header/footer:', error);
        if (options.onError) {
            options.onError(error);
        }
        throw error;
    }
};
